import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `note`, in the terminal register — the ambient line, and the separator.
 *
 * Two faces, matching the contract exactly. `text` is a dim line that does
 * not compete with the transcript; `rule` is a labelled horizontal rule drawn
 * the way a terminal draws one, with the label sitting IN the rule rather
 * than above it, because a monospace column has no other way to centre
 * something without spending a second line. */
@component({ name: "std-term-turn-note" })
export class TermTurnNote extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");
  private $rule: HTMLElement = this.$("rule");

  static makeWith(props: TurnSlots.Props["note"]): TermTurnNote {
    return (TermTurnNote.make() as TermTurnNote).bind(props);
  }

  bind(props: TurnSlots.Props["note"]): this {
    const isRule = props.face === "rule";
    this.$rule.style.display = isRule ? "" : "none";
    this.$text.style.display = isRule ? "none" : "";
    if (props.face === "rule") {
      const label = props.label ?? "";
      this.$rule.textContent = label === "" ? "" : ` ${label} `;
      this.$rule.classList.toggle("bare", label === "");
      return this;
    }
    this.$text.textContent = props.text;
    // `active` is the harness working right now. It gets the one moving
    // thing in this register — a terminal's whole vocabulary of emphasis is
    // brightness, so spending it anywhere else would spend the effect.
    this.$text.classList.toggle("active", props.active === true);
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-ter, #8b93a7);
      }
      #text {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      #text.active {
        color: var(--text-sec, #5c6370);
      }
      /* The label rides the rule: pseudo-elements draw the dashes on either
         side, so no width has to be computed and the rule reflows for free. */
      #rule {
        display: flex;
        align-items: center;
        gap: 0;
        white-space: pre;
        color: var(--text-ter, #8b93a7);
      }
      #rule::before,
      #rule::after {
        content: "";
        flex: 1;
        border-top: 1px solid var(--divider, #d8dee9);
      }
      #rule.bare::before,
      #rule.bare::after {
        flex: 1;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="text"></div>
    <div id="rule"></div>
  `;
}
